import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, FieldProps, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import { MedicamentEnumDto, ProcedureTaxPoint, UpsertProcedureDto, VatDto } from 'ts-types/api.types';
import { composeValidators, maxChars, mustBeNumber, required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import TextArea from 'components/final-form/TextArea';
import Select from 'components/final-form/Select';
import styled from 'styled-components';
import { getAllVats } from 'services/vatGroupServices';
import arrayMutators from 'final-form-arrays';
import { addProcedure, editProcedure, getAllTariffCodesForProcedure } from 'services/procedureServices';
import CheckBox from 'components/final-form/CheckBox';
import RadioGroup from 'components/final-form/RadioGroup';
import ProcedureTaxPointsSegment from 'components/procedures/segments/ProcedureTaxPointsSegment';
import { findLatestTaxPoint } from 'components/procedures/utils/procedureUtils';
import { ProcedureType } from 'ts-types/api.enums';
import { numberFormatter } from 'utils/fomattingUtils';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import DatePicker from 'components/final-form/DatePicker';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';

const StyledGridColumn = styled(Grid.Column)`
  padding-top: 1rem !important;
`;

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  procedureId?: number,
  procedure?: UpsertProcedureDto
  tariff?: number,
  tarmedTariff?: Boolean,
}

interface State {
  procedure?: Partial<UpsertProcedureDto>,
  errorMessage?: string,
  vats?: Array<VatDto>,
  types?: Array<RadioProps>,
  tariffCodes?: Array<MedicamentEnumDto>,
  cancelTokenSource: CancelTokenSource
}

const VALIDATED_FIELDS: Array<string> = [
  "code",
  "description",
  "descriptionFr",
  "descriptionIt",
  "descriptionEn"
];

const SECONDARY_VALIDATED_FIELDS: Array<string> = [
  "shortDesc",
  "shortDescFr",
  "shortDescIt",
  "shortDescEn"
];

const ACCEPTED_TARIFF_CODES: Array<string> = [
  "311",
  "312",
  "316",
  "317",
  "351",
  "406",
  "590",
  "530"
];

const initialProcedure: Partial<UpsertProcedureDto> = {
  type: ProcedureType.TP,
  askAmount: false,
  variable: false,
  fromAmount: 0,
  toAmount: 0,
  vatId: 1,
  procedureTaxPoints: [],
  tariffCode: "0"
};

class UpsertProcedureForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, procedure, tariff} = this.props;

    const typesLabels: Array<string> = ["tp", "chf"];

    //@ts-ignore
    initialProcedure.fromAmount = numberFormatter.format(initialProcedure.fromAmount);

    //@ts-ignore
    initialProcedure.toAmount = numberFormatter.format(initialProcedure.toAmount);
    initialProcedure.tariffId = tariff;

    if (procedure && procedure.tariffCode == null) {
      procedure.tariffCode = "0";
    }

    const types = generateRadioButtonOptions(typesLabels, "procedure.type", getMessage, "procedure.type", Object.values(ProcedureType))
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      procedure: procedure ? procedure : initialProcedure,
      vats: [],
      types: types,
      tariffCodes: [],
      cancelTokenSource
    };

    getAllVats(cancelTokenSource)
        .then(response => {
          initialProcedure.vatId = response[0].id;

          let vats: Array<VatDto>;
          if (procedure) {
            vats = response.filter(vat => vat.id === procedure.vatId || !vat.deleted)
          } else {
            vats = response.filter(vat => !vat.deleted);
          }

          this.setState({
            vats: vats
          })
        }).catch(noop);

    getAllTariffCodesForProcedure(cancelTokenSource)
        .then(response => {
          this.setState((prevState) => {
            const tariffCodes = response.filter(tc => ACCEPTED_TARIFF_CODES.includes(tc.code));
            tariffCodes.unshift({code: "0", name: `${getMessage('tc.unknown')}`});
            return {
              tariffCodes: tariffCodes
            };
          });
        }).catch(noop);

    if (procedure) {

      if (procedure.fromAmount) {
        //@ts-ignore
        procedure.fromAmount = numberFormatter.format(procedure.fromAmount);
      }

      if (procedure.toAmount) {
        //@ts-ignore
        procedure.toAmount = numberFormatter.format(procedure.toAmount);
      }

      if (procedure.lab) {
        //@ts-ignore
        procedure.lab = numberFormatter.format(procedure.lab);
      }

      if (procedure.material) {
        //@ts-ignore
        procedure.material = numberFormatter.format(procedure.material);
      }

      if (procedure.percentage) {
        //@ts-ignore
        procedure.percentage = numberFormatter.format(procedure.percentage);
      }
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  handleChange = ({values}: FormState, form: FormApi) => {

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  handleSubmit = async (values: Partial<UpsertProcedureDto>) => {

    const {procedureId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;
    const latestTaxPoint = findLatestTaxPoint(values.procedureTaxPoints!);

    if (values.tariffCode == "0") {
      values.tariffCode = undefined;
    }

    if (latestTaxPoint) {
      values.fromAmount = latestTaxPoint.fromAmount;
      if (latestTaxPoint.toAmount > 0) {
        values.toAmount = latestTaxPoint.toAmount;
      }
    } else {
      values.toAmount = values.fromAmount;
    }

    const request: Partial<UpsertProcedureDto> = {
      ...values,
      shortDesc: values.description,
      shortDescEn: values.descriptionEn,
      shortDescFr: values.descriptionFr,
      shortDescIt: values.descriptionIt
    }

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (procedureId) {
      await editProcedure(procedureId, request, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addProcedure(request, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {procedure} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={procedure}
            mutators={{
              ...arrayMutators
            }}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel, procedure, tarmedTariff} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="695px">
                  {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
                  }
                  <Grid.Row>
                    <Grid.Column>
                      {this.renderDescriptionSection()}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <StyledGridColumn width={8}>
                      {this.renderValueSection(values.variable, values.procedureTaxPoints, values.fromDate)}
                    </StyledGridColumn>
                    <StyledGridColumn width={8}>
                      <ProcedureTaxPointsSegment procedureTaxPoint={
                        procedure ? procedure.procedureTaxPoints : undefined
                      }
                                                 tarmedTariff={tarmedTariff}
                                                 tariffCode={procedure && procedure.tariffCode}
                      />
                    </StyledGridColumn>
                  </Grid.Row>
                  <Grid.Row>
                    <StyledGridColumn>
                      {this.renderAdditionalDescSection()}
                    </StyledGridColumn>
                  </Grid.Row>
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </form>
    );
  };

  renderDescriptionSection = (): React.ReactNode => {

    const {getMessage, tarmedTariff, procedure} = this.props;
    const {tariffCodes} = this.state;

    const descriptionRows: Array<Array<string>> = [
      ["code"],
      ["description"],
      ["descriptionFr"],
      ["descriptionIt"],
      ["descriptionEn"]
    ];

    const internalDescriptionRows: Array<Array<string>> = [
      ["shortDesc"],
      ["shortDescFr"],
      ["shortDescIt"],
      ["shortDescEn"]
    ];

    const validator = (value: string) => required(value);
    const codeValidator = composeValidators(maxChars(10), required);
    const descriptionSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {

                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? field === "code" ? codeValidator : validator : undefined,
                  component: Input,
                  disabled: (procedure && procedure.tariffCode == "001")
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">

                        {
                          field === "code" ?
                              `${getMessage(field)}:` :
                              `${getMessage(`procedure.${field}`)}:`
                        }
                      </Grid.Column>
                      <Grid.Column width={fieldProps.name === "code" ? 5 : 11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    const internalDescriptionSection = internalDescriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: SECONDARY_VALIDATED_FIELDS.includes(field) ? validator : undefined,
                  component: Input
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">
                        {`${getMessage(`procedure.internal.${field}`)}:`}
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    let tariffCodesOptions = tariffCodes!.map((trCode, index) => {

      if (index == 0) {
        return {
          key: 0,
          text: getMessage('tc.unknown'),
          value: "0"
        }
      }
      return {
        key: (trCode.code),
        text: `${trCode.code}, ${getMessage(`tc.${trCode.name}`)}`,
        value: trCode.code
      }
    });

    if (procedure && procedure.tariffCode === "001") {
      tariffCodesOptions = [
        {
          key: 0,
          text: `001, ${getMessage('procedure.tarmed')}`,
          value: "001"
        }
      ]
    }

    const tariffCodesDropdown = (

        <Grid.Row>
          <Grid.Column width={5}>
            Tariff Codes:
          </Grid.Column>
          <Grid.Column width={6}>
            <Field
                name="tariffCode"
                component={Select}
                fluid
                options={tariffCodesOptions}
                disabled={procedure && procedure.tariffCode == "001"}
            />
          </Grid.Column>
        </Grid.Row>

    );

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as='h5'>
                {getMessage("description")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {descriptionSection}
          {tarmedTariff && internalDescriptionSection}
          {tarmedTariff && tariffCodesDropdown}
        </PraxFormGrid>
    );
  };

  renderValueSection = (variable: Boolean, procedureTaxPoints: Array<ProcedureTaxPoint>, fromDate: string): React.ReactNode => {

    const {vats, procedure, types} = this.state;
    const {getMessage} = this.props;

    const vatOptions = vats!.map(vat => {

      let vatType = "excl.";
      if (vat.type === 0) {
        vatType = "incl."
      }
      return {
        key: (vat.id),
        text: `${vatType} ${numberFormatter.format(vat.value)}`,
        value: vat.id
      }
    });

    const disabledBefore = fromDate && new Date(fromDate);

    const latestTaxPoint = findLatestTaxPoint(procedureTaxPoints);

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column width={9}>
              <Header as='h5'>
                {getMessage("value")}
              </Header>
            </Grid.Column>
            <Grid.Column width={7}>
              <Header as='h5'>
                {getMessage("procedure.validity")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={4}>
              <Field name="variable"
                     component={CheckBox}
                     label={getMessage("procedure.variable.value")}
                     disabledWhenFalse={procedure && procedure.tariffCode == "001"}
              />

            </Grid.Column>
            <Grid.Column width={4}>
              <Field name="askAmount"
                     component={CheckBox}
                     label={getMessage("procedure.ask.amount")}
                     disabledWhenFalse={procedure && procedure.tariffCode == "001"}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {
                !variable ?
                    `${getMessage("value")}: ` : `${getMessage("from")}: `
              }
              {
                latestTaxPoint && procedure!.fromAmount != latestTaxPoint.fromAmount ?
                    latestTaxPoint.fromAmount != null ?
                        numberFormatter.format(latestTaxPoint.fromAmount) :
                        numberFormatter.format(0) :
                    procedure && procedure.fromAmount ? procedure.fromAmount : numberFormatter.format(0)
              }
            </Grid.Column>
            <Grid.Column width={2} verticalAlign="middle">
              {`${getMessage("procedure.lab")}:`}
            </Grid.Column>
            <Grid.Column width={4} verticalAlign="middle">
              <Field name="lab"
                     fluid
                     component={Input}
                     validate={mustBeNumber}
                     disabled={procedure && procedure.tariffCode == "001"}
              />
            </Grid.Column>
            <React.Fragment>
              <Grid.Column width={2} verticalAlign="middle">
                {`${getMessage("from")}:`}
              </Grid.Column>
              <Grid.Column width={5} verticalAlign="middle">
                <Field name="fromDate"
                       component={DatePicker}
                       fluid
                />
              </Grid.Column>
            </React.Fragment>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {
                variable ?
                    `${getMessage("to")}: ${
                        latestTaxPoint && procedure!.toAmount != latestTaxPoint.toAmount ?
                            latestTaxPoint.toAmount != null ?
                                numberFormatter.format(latestTaxPoint.toAmount) :
                                numberFormatter.format(0) :
                            procedure && procedure.toAmount ? procedure.toAmount : numberFormatter.format(0)
                    }` :
                    undefined
              }

            </Grid.Column>
            <Grid.Column width={2} verticalAlign="middle">
              {`${getMessage("procedure.material")}:`}
            </Grid.Column>
            <Grid.Column width={4} verticalAlign="middle">
              <Field name="material"
                     fluid
                     component={Input}
                     validate={mustBeNumber}
                     disabled={procedure && procedure.tariffCode == "001"}
              />
            </Grid.Column>
            <React.Fragment>
              <Grid.Column width={2} verticalAlign="middle">
                {`${getMessage("to")}:`}
              </Grid.Column>
              <Grid.Column width={5} verticalAlign="middle">
                <Field name="toDate"
                       component={DatePicker}
                       disabledBefore={disabledBefore}
                       fluid
                />
              </Grid.Column>
            </React.Fragment>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <Field
                  name="type"
                  component={RadioGroup}
                  radioDefinitions={types}
                  radiowidth="50%"
                  disabled={procedure && procedure.tariffCode == "001"}
              />
            </Grid.Column>
            <Grid.Column width={2} verticalAlign="middle">
              {`${getMessage("vatId")}:`}
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="vatId"
                  component={Select}
                  fluid
                  options={vatOptions}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderAdditionalDescSection = (): React.ReactNode => {

    const {getMessage, procedure} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as='h5'>
                {getMessage("procedure.additional.description")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Field name="special"
                     component={TextArea}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={6} verticalAlign="middle">
              {`${getMessage("procedure.percentage")}:`}
            </Grid.Column>
            <Grid.Column width={8} verticalAlign="middle">
              <Field name="percentage"
                     component={Input}
                     validate={mustBeNumber}
                     disabled={procedure && procedure.tariffCode == "001"}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  }

}

export default withMessages(UpsertProcedureForm);
